import type { BTreeEdge, BTreeNode, BTreeSpec, Box, ResolvedFigure } from '../model.js';
import { box, finishFigure, point, roundGeometry, SceneBuilder, validateViewport } from '../scene-builder.js';

const TOP = 58;
const BOTTOM = 28;
const FORBIDDEN_PRESENTATION_KEYS = new Set([
  'x', 'y', 'cx', 'cy', 'x1', 'x2', 'y1', 'y2', 'd', 'points', 'box',
  'fill', 'stroke', 'color', 'fontSize', 'fontFamily', 'duration',
]);

interface Bound { value: number; inclusive: boolean }
interface Range { lower?: Bound; upper?: Bound }

export interface BTreeLayout {
  nodeById: Map<string, BTreeNode>;
  childrenById: Map<string, BTreeEdge[]>;
  depthById: Map<string, number>;
  orderedNodeIds: string[];
  xById: Map<string, number>;
  searchNodeIds: string[];
  searchEdgeIds: string[];
  foundNodeId: string;
  foundKeyIndex: number;
  maxDepth: number;
  leafCount: number;
  maxKeys: number;
}

export function btreeNodeId(nodeId: string): string {
  return `btree.node.${nodeId}`;
}

export function btreeKeyId(nodeId: string, key: number): string {
  return `btree.key.${nodeId}.${key}`;
}

export function btreeRecordId(nodeId: string, key: number): string {
  return `btree.record.${nodeId}.${key}`;
}

export function btreeEdgeId(from: string, to: string): string {
  return `btree.edge.${from}.${to}`;
}

/** Compile an ordered B+-tree index without accepting any authored geometry. */
export function compileBTree(spec: BTreeSpec): ResolvedFigure {
  const layout = validateBTree(spec);
  const builder = new SceneBuilder();
  const { width, height } = spec.viewport;
  const narrow = width <= 390;
  const margin = width <= 320 ? 10 : narrow ? 14 : 24;
  const usableWidth = width - margin * 2;
  const leafSlot = usableWidth / layout.leafCount;
  const gap = narrow ? 4 : 10;
  const cellWidth = clamp((leafSlot - gap) / layout.maxKeys, narrow ? 16 : 20, narrow ? 27 : 34);
  const nodeHeight = narrow ? 58 : 64;
  const keyHeight = narrow ? 29 : 32;
  const levelStep = layout.maxDepth === 0 ? 0 : (height - BOTTOM - nodeHeight - TOP) / layout.maxDepth;
  const boundsById = new Map<string, Box>();
  const warnings: string[] = [];

  builder.text('btree.eyebrow', spec.id, 'annotation', point(margin, 25), `B-TREE LOOKUP · KEY ${spec.searchKey}`, {
    size: 10, mono: true, weight: 700, uppercase: true,
  });

  for (const nodeId of layout.orderedNodeIds) {
    const node = layout.nodeById.get(nodeId)!;
    const visualWidth = Math.max(cellWidth * node.keys.length, narrow ? 34 : 42);
    const centerX = margin + layout.xById.get(nodeId)! * usableWidth;
    const centerY = TOP + layout.depthById.get(nodeId)! * levelStep + nodeHeight / 2;
    boundsById.set(nodeId, box(centerX - visualWidth / 2, centerY - nodeHeight / 2, visualWidth, nodeHeight));
  }

  // Connections precede cards so ordered nodes remain legible above routing.
  for (const parentId of layout.orderedNodeIds) {
    for (const edge of layout.childrenById.get(parentId)!) {
      const parent = boundsById.get(edge.from)!;
      const child = boundsById.get(edge.to)!;
      const semanticId = btreeEdgeId(edge.from, edge.to);
      const start = point(parent.x + parent.width / 2, parent.y + parent.height);
      const end = point(child.x + child.width / 2, child.y);
      const midY = roundGeometry((start.y + end.y) / 2);
      const isSearch = layout.searchEdgeIds.includes(semanticId);
      const label = compactRange(edge.range);
      const labelWidth = clamp(label.length * (narrow ? 4.8 : 5.6) + 12, 34, narrow ? 58 : 74);
      const labelX = clamp((start.x + end.x) / 2, labelWidth / 2 + 2, width - labelWidth / 2 - 2);

      builder.path(`${semanticId}.path`, semanticId, isSearch ? 'pointer' : 'grid', `M ${n(start.x)} ${n(start.y)} V ${n(midY)} H ${n(end.x)} V ${n(end.y)}`, {
        markerEnd: true,
        label: edge.range,
      });
      builder.rect(`${semanticId}.range-plate`, semanticId, isSearch ? 'active' : 'surface', box(labelX - labelWidth / 2, midY - 9, labelWidth, 18), {
        radius: 8,
        label: edge.range,
      });
      builder.text(`${semanticId}.range`, semanticId, 'annotation', point(labelX, midY + 3), label, {
        size: narrow ? 7.5 : 8.5, mono: true, weight: 700, align: 'middle',
      });
      builder.anchor(`${semanticId}.source`, semanticId, start, 'down');
      builder.anchor(`${semanticId}.mid`, semanticId, point(labelX, midY));
      builder.anchor(`${semanticId}.target`, semanticId, end, 'down');
      builder.hit(semanticId, touchBounds(start, end, width, height));
      builder.relation(`${semanticId}.partitions`, 'partitions', btreeNodeId(edge.from), btreeNodeId(edge.to), edge.range);
    }
  }

  for (const nodeId of layout.orderedNodeIds) {
    const node = layout.nodeById.get(nodeId)!;
    const bounds = boundsById.get(nodeId)!;
    const semanticId = btreeNodeId(node.id);
    const onSearchPath = layout.searchNodeIds.includes(node.id);

    builder.rect(`${semanticId}.surface`, semanticId, onSearchPath ? 'operator' : 'surface', bounds, {
      radius: 5,
      label: `${node.leaf ? 'leaf' : 'branch'} node ${node.id}; keys ${node.keys.join(', ')}`,
      interactive: true,
    });
    node.keys.forEach((key, index) => {
      const keySemanticId = btreeKeyId(node.id, key);
      const keyBounds = box(bounds.x + index * cellWidth, bounds.y, cellWidth, keyHeight);
      const isSearchedKey = node.id === layout.foundNodeId && index === layout.foundKeyIndex;
      builder.rect(`${keySemanticId}.cell`, keySemanticId, isSearchedKey ? 'active' : onSearchPath ? 'header' : 'slot', keyBounds, {
        radius: index === 0 || index === node.keys.length - 1 ? 3 : 0,
        label: `separator key ${key}`,
        interactive: true,
      });
      builder.text(`${keySemanticId}.value`, keySemanticId, 'structure', point(keyBounds.x + keyBounds.width / 2, keyBounds.y + keyHeight / 2 + 4), String(key), {
        size: narrow ? 9 : 11, mono: true, weight: 700, align: 'middle',
      });
      builder.anchor(`${keySemanticId}.center`, keySemanticId, point(keyBounds.x + keyBounds.width / 2, keyBounds.y + keyBounds.height / 2));
      builder.hit(keySemanticId, expandTouch(keyBounds, width, height));
      if (index > 0) {
        builder.relation(`${keySemanticId}.ordered-after`, 'ordered-after', btreeKeyId(node.id, node.keys[index - 1]!), keySemanticId, `${key} is greater than ${node.keys[index - 1]}`);
      }
    });

    if (node.leaf) {
      const recordText = node.records!.join(' · ');
      const shown = compact(recordText, Math.max(6, Math.floor((bounds.width - 7) / (narrow ? 4.5 : 5.1))));
      builder.text(`${semanticId}.record-summary`, semanticId, 'evidence', point(bounds.x + bounds.width / 2, bounds.y + keyHeight + 17), shown, {
        size: narrow ? 7 : 8, mono: true, weight: 700, align: 'middle',
      });
      node.keys.forEach((key, index) => {
        const recordSemanticId = btreeRecordId(node.id, key);
        builder.anchor(`${recordSemanticId}.evidence`, recordSemanticId, point(bounds.x + cellWidth * (index + 0.5), bounds.y + keyHeight + 15), 'down');
        builder.hit(recordSemanticId, expandTouch(box(bounds.x + index * cellWidth, bounds.y + keyHeight, cellWidth, bounds.height - keyHeight), width, height));
        builder.relation(`${recordSemanticId}.indexed-by`, 'indexed-by', btreeKeyId(node.id, key), recordSemanticId, node.records![index]!);
      });
      if (shown !== recordText) warnings.push(`Leaf evidence compacted: ${node.id}`);
    } else {
      builder.text(`${semanticId}.kind`, semanticId, 'annotation', point(bounds.x + bounds.width / 2, bounds.y + keyHeight + 17), 'BRANCH', {
        size: narrow ? 7 : 8, mono: true, weight: 700, align: 'middle', uppercase: true,
      });
    }
    builder.anchor(`${semanticId}.top`, semanticId, point(bounds.x + bounds.width / 2, bounds.y), 'up');
    builder.anchor(`${semanticId}.center`, semanticId, point(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2));
    builder.anchor(`${semanticId}.bottom`, semanticId, point(bounds.x + bounds.width / 2, bounds.y + bounds.height), 'down');
    builder.hit(semanticId, expandTouch(bounds, width, height));
  }

  const foundNode = layout.nodeById.get(layout.foundNodeId)!;
  const foundKey = foundNode.keys[layout.foundKeyIndex]!;
  const active = [
    ...layout.searchNodeIds.map(btreeNodeId),
    ...layout.searchEdgeIds,
    btreeKeyId(layout.foundNodeId, foundKey),
    btreeRecordId(layout.foundNodeId, foundKey),
    ...(spec.emphasis ?? []),
  ];
  if (spec.focusNodeId) active.push(btreeNodeId(spec.focusNodeId));
  if (cellWidth < 18) warnings.push('Key cells use the dense phone policy at this width.');

  return finishFigure(spec, builder, {
    title: `A B-tree lookup discards whole key ranges`,
    subtitle: `Compare ${spec.searchKey} with ordered separators, follow one child range, then verify the leaf record.`,
    state: `found:${btreeKeyId(layout.foundNodeId, foundKey)}${spec.focusNodeId ? ` · focus:${btreeNodeId(spec.focusNodeId)}` : ''}`,
    active: [...new Set(active)],
    warnings: [...new Set(warnings)],
  });
}

/** Validate ordering, topology, range partitions, balance, and lookup truth. */
export function validateBTree(spec: BTreeSpec): BTreeLayout {
  validateViewport(spec.viewport.width, spec.viewport.height);
  assertCoordinateFree(spec);
  if (!spec.provenance.trim()) throw new Error('btree: provenance is required');
  if (!Number.isFinite(spec.searchKey)) throw new Error('btree: searchKey must be finite');
  if (spec.nodes.length < 3 || spec.nodes.length > 15) throw new Error('btree: require 3…15 nodes');
  if (spec.edges.length !== spec.nodes.length - 1) throw new Error('btree: a tree requires exactly nodes.length - 1 edges');

  const nodeById = new Map<string, BTreeNode>();
  for (const node of spec.nodes) {
    if (!validSemanticId(node.id)) throw new Error(`btree: invalid node id ${node.id}`);
    if (nodeById.has(node.id)) throw new Error(`btree: duplicate node id ${node.id}`);
    if (node.keys.length < 1 || node.keys.length > 4) throw new Error(`btree: node ${node.id} requires 1…4 keys`);
    if (node.keys.some((key) => !Number.isFinite(key))) throw new Error(`btree: node ${node.id} has a non-finite key`);
    if (node.keys.some((key, index) => index > 0 && key <= node.keys[index - 1]!)) throw new Error(`btree: keys in node ${node.id} must be strictly sorted`);
    if (node.leaf) {
      if (!node.records || node.records.length !== node.keys.length || node.records.some((record) => !record.trim())) {
        throw new Error(`btree: leaf ${node.id} requires one non-blank record per key`);
      }
    } else if (node.records !== undefined) {
      throw new Error(`btree: branch ${node.id} must not contain records`);
    }
    nodeById.set(node.id, node);
  }
  if (!nodeById.has(spec.rootId)) throw new Error(`btree: root ${spec.rootId} does not exist`);
  if (spec.focusNodeId && !nodeById.has(spec.focusNodeId)) throw new Error(`btree: focus node ${spec.focusNodeId} does not exist`);

  const parents = new Map<string, string[]>();
  const childrenById = new Map<string, BTreeEdge[]>([...nodeById.keys()].map((id) => [id, []]));
  const edgePairs = new Set<string>();
  for (const edge of spec.edges) {
    if (!nodeById.has(edge.from) || !nodeById.has(edge.to)) throw new Error(`btree: edge ${edge.from} → ${edge.to} references an unknown node`);
    if (edge.from === edge.to) throw new Error(`btree: self edge at ${edge.from}`);
    const pair = `${edge.from}\u0000${edge.to}`;
    if (edgePairs.has(pair)) throw new Error(`btree: duplicate edge ${edge.from} → ${edge.to}`);
    edgePairs.add(pair);
    parseRange(edge.range);
    parents.set(edge.to, [...(parents.get(edge.to) ?? []), edge.from]);
    childrenById.get(edge.from)!.push(edge);
  }
  if ((parents.get(spec.rootId) ?? []).length) throw new Error('btree: root must not have a parent');
  for (const id of nodeById.keys()) {
    if (id !== spec.rootId && (parents.get(id) ?? []).length !== 1) throw new Error(`btree: non-root node ${id} must have exactly one parent`);
  }

  for (const [nodeId, edges] of childrenById) {
    const node = nodeById.get(nodeId)!;
    if (node.leaf && edges.length) throw new Error(`btree: leaf ${nodeId} must not have children`);
    if (!node.leaf && edges.length !== node.keys.length + 1) throw new Error(`btree: branch ${nodeId} requires keys.length + 1 children`);
    edges.sort((a, b) => compareRanges(parseRange(a.range), parseRange(b.range)) || a.to.localeCompare(b.to));
    edges.forEach((edge, index) => {
      const expected = expectedRange(node.keys, index);
      if (edge.range !== expected) throw new Error(`btree: edge ${edge.from} → ${edge.to} range must be ${expected}`);
    });
  }

  const depthById = new Map<string, number>([[spec.rootId, 0]]);
  const orderedNodeIds: string[] = [];
  const queue = [spec.rootId];
  while (queue.length) {
    const id = queue.shift()!;
    orderedNodeIds.push(id);
    for (const edge of childrenById.get(id)!) {
      if (depthById.has(edge.to)) throw new Error(`btree: cycle or repeated child detected at ${edge.to}`);
      depthById.set(edge.to, depthById.get(id)! + 1);
      queue.push(edge.to);
    }
  }
  if (orderedNodeIds.length !== spec.nodes.length) throw new Error('btree: nodes must form one connected tree rooted at rootId');
  const maxDepth = Math.max(...depthById.values());
  if (maxDepth < 1 || maxDepth > 3) throw new Error('btree: schematic supports 2…4 levels');
  const leafDepths = new Set(orderedNodeIds.filter((id) => nodeById.get(id)!.leaf).map((id) => depthById.get(id)!));
  if (leafDepths.size !== 1) throw new Error('btree: every leaf must be at the same depth');

  validateSubtreeRanges(spec.rootId, {}, nodeById, childrenById);

  const leaves: string[] = [];
  const visitLeaves = (id: string): void => {
    const children = childrenById.get(id)!;
    if (!children.length) leaves.push(id);
    else children.forEach((edge) => visitLeaves(edge.to));
  };
  visitLeaves(spec.rootId);
  const leafX = new Map(leaves.map((id, index) => [id, leaves.length === 1 ? 0.5 : (index + 0.5) / leaves.length]));
  const xById = new Map<string, number>();
  const resolveX = (id: string): number => {
    const cached = xById.get(id);
    if (cached !== undefined) return cached;
    const children = childrenById.get(id)!;
    const value = children.length ? children.reduce((sum, edge) => sum + resolveX(edge.to), 0) / children.length : leafX.get(id)!;
    xById.set(id, roundGeometry(value));
    return value;
  };
  resolveX(spec.rootId);

  const searchNodeIds: string[] = [];
  const searchEdgeIds: string[] = [];
  let current = spec.rootId;
  while (true) {
    searchNodeIds.push(current);
    const node = nodeById.get(current)!;
    if (node.leaf) break;
    const index = childIndex(node.keys, spec.searchKey);
    const edge = childrenById.get(current)![index]!;
    searchEdgeIds.push(btreeEdgeId(edge.from, edge.to));
    current = edge.to;
  }
  const foundNode = nodeById.get(current)!;
  const foundKeyIndex = foundNode.keys.indexOf(spec.searchKey);
  if (foundKeyIndex < 0) throw new Error(`btree: searchKey ${spec.searchKey} is not evidenced by its routed leaf`);

  return {
    nodeById, childrenById, depthById, orderedNodeIds, xById, searchNodeIds, searchEdgeIds,
    foundNodeId: current, foundKeyIndex, maxDepth, leafCount: leaves.length,
    maxKeys: Math.max(...spec.nodes.map((node) => node.keys.length)),
  };
}

function validateSubtreeRanges(
  nodeId: string,
  inherited: Range,
  nodes: Map<string, BTreeNode>,
  children: Map<string, BTreeEdge[]>,
): void {
  const node = nodes.get(nodeId)!;
  for (const key of node.keys) {
    if (!within(key, inherited)) throw new Error(`btree: key ${key} in ${nodeId} violates its ancestor range`);
  }
  for (const edge of children.get(nodeId)!) {
    validateSubtreeRanges(edge.to, intersect(inherited, parseRange(edge.range)), nodes, children);
  }
}

function expectedRange(keys: number[], index: number): string {
  if (index === 0) return `(-∞, ${keys[0]})`;
  if (index === keys.length) return `[${keys.at(-1)}, +∞)`;
  return `[${keys[index - 1]}, ${keys[index]})`;
}

function parseRange(value: string): Range {
  const lowerOnly = /^\(-∞, (-?\d+(?:\.\d+)?)\)$/.exec(value);
  if (lowerOnly) return { upper: { value: Number(lowerOnly[1]), inclusive: false } };
  const upperOnly = /^\[(-?\d+(?:\.\d+)?), \+∞\)$/.exec(value);
  if (upperOnly) return { lower: { value: Number(upperOnly[1]), inclusive: true } };
  const finite = /^\[(-?\d+(?:\.\d+)?), (-?\d+(?:\.\d+)?)\)$/.exec(value);
  if (finite) {
    const lower = Number(finite[1]);
    const upper = Number(finite[2]);
    if (lower >= upper) throw new Error(`btree: invalid range ${value}`);
    return { lower: { value: lower, inclusive: true }, upper: { value: upper, inclusive: false } };
  }
  throw new Error(`btree: invalid canonical range ${value}`);
}

function intersect(a: Range, b: Range): Range {
  const lower = !a.lower ? b.lower : !b.lower ? a.lower : a.lower.value >= b.lower.value ? a.lower : b.lower;
  const upper = !a.upper ? b.upper : !b.upper ? a.upper : a.upper.value <= b.upper.value ? a.upper : b.upper;
  if (lower && upper && lower.value >= upper.value) throw new Error('btree: child range does not intersect its ancestor range');
  return { ...(lower ? { lower } : {}), ...(upper ? { upper } : {}) };
}

function within(value: number, range: Range): boolean {
  return (!range.lower || value > range.lower.value || (range.lower.inclusive && value === range.lower.value))
    && (!range.upper || value < range.upper.value || (range.upper.inclusive && value === range.upper.value));
}

function childIndex(keys: number[], value: number): number {
  const index = keys.findIndex((key) => value < key);
  return index < 0 ? keys.length : index;
}

function compareRanges(a: Range, b: Range): number {
  return (a.lower?.value ?? Number.NEGATIVE_INFINITY) - (b.lower?.value ?? Number.NEGATIVE_INFINITY);
}

function compactRange(value: string): string {
  return value.replace(/^\(-∞, (.+)\)$/, '<$1').replace(/^\[(.+), \+∞\)$/, '≥$1').replace(/^\[(.+), (.+)\)$/, '$1–$2');
}

function compact(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(2, max - 1))}…`;
}

function expandTouch(bounds: Box, width: number, height: number): Box {
  const targetWidth = Math.max(44, bounds.width);
  const targetHeight = Math.max(44, bounds.height);
  return box(
    clamp(bounds.x + bounds.width / 2 - targetWidth / 2, 0, width - targetWidth),
    clamp(bounds.y + bounds.height / 2 - targetHeight / 2, 0, height - targetHeight),
    targetWidth,
    targetHeight,
  );
}

function touchBounds(start: { x: number; y: number }, end: { x: number; y: number }, width: number, height: number): Box {
  const targetWidth = Math.max(44, Math.abs(start.x - end.x) + 14);
  const targetHeight = Math.max(44, Math.abs(start.y - end.y));
  return box(
    clamp((start.x + end.x - targetWidth) / 2, 0, width - targetWidth),
    clamp((start.y + end.y - targetHeight) / 2, 0, height - targetHeight),
    targetWidth,
    targetHeight,
  );
}

function assertCoordinateFree(value: unknown, path = 'spec'): void {
  if (!value || typeof value !== 'object') return;
  for (const [key, child] of Object.entries(value)) {
    if (FORBIDDEN_PRESENTATION_KEYS.has(key)) throw new Error(`btree: authored presentation field ${path}.${key} is forbidden`);
    assertCoordinateFree(child, `${path}.${key}`);
  }
}

function validSemanticId(id: string): boolean {
  return /^[a-z][a-z0-9-]*$/.test(id);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function n(value: number): number {
  return Math.round(value * 10) / 10;
}
